import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  TFacetQueryType,
  ERoute,
  EStudioTabView,
  ModalType,
} from "../../@types";
import { StudioViewModel, validSearch, queryToUrl } from "./ViewModel";
import { RemixView } from "./RemixView";
import { StudioView } from "./StudioView";

import { getQueryParam } from "../../Utilities/QueryParam";
import { URLS } from "../../../utils";
import { hasScope } from "../../Utilities/Studio";
import { useRecoilState } from "recoil";
import { StudioSession } from "../../State/studioSession";
import { isArray } from "@apollo/client/cache/inmemory/helpers";
import { useIsMobileApp } from "../../Hooks/useIsMobileApp";
import { AllstarModalState } from "../../State/modals";

// import { CardView } from "./__CardView";

type TStudioPageRoute = {
  route: ERoute;
};

export default function StudioPage({ route }: TStudioPageRoute) {
  const { momentId }: any = useParams();
  const pageNav = route === "remix" ? `/remix/${momentId}` : "/studio";
  const [creatorCardModal, setCreatorCardModal] = useState(false);
  const [modal, setModal] = useState({ type: "" });
  const [banner, setBanner] = useState<any>("");
  const { isMobileApp } = useIsMobileApp();
  const [studioSession, setStudioSession] = useRecoilState<any>(StudioSession);
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  // Set the graphQl query here:
  // if the user lands on the page we pull in the params.
  const [query, setQuery] = useState<TFacetQueryType>(() => {
    try {
      const predefinedPath: any = {};

      for (const param of validSearch) {
        if (param && getQueryParam(param)) {
          predefinedPath[param] = JSON.parse(getQueryParam(param) || "");
        }
      }

      setBanner(
        predefinedPath?.type?.length === 1 ? predefinedPath.type[0] : "default",
      );
      return predefinedPath as TFacetQueryType;
    } catch (error) {
      // console.log("Invalid query param;");
      // console.log("Removing query", error);
      window.history.replaceState(null, "", `${URLS.STUDIO}`);
      return {};
    }
  });

  const {
    allstarUser,
    getFreshProfile,
    logout,
    toggleCardState,
    remixData,
    postRemix,
    getCreatorCardClips,
    facetRefetch,
    facetLoading,
    singleCardData,
    setSingleCardData,
    cardClips,
    cardsData,
    mutateFollowers,
    clearMyEnabledCards,
  } = StudioViewModel(route, query);

  useEffect(() => {
    setAllstarModalState((allstarModalState) => ({
      ...allstarModalState,
      isOpen: ModalType.StudioWelcome,
    }));
  }, [setAllstarModalState]);

  useEffect(() => {
    if (allstarUser.loggedIn && !allstarUser?.user?.hasUsername) {
      setModal({ type: "username" });
    }
  }, [allstarUser]);

  useEffect(() => {
    if (singleCardData.id) {
      setCreatorCardModal(true);
    }
  }, [singleCardData.id]);

  // Handle card click on idividual cards, Open modal and set the Creator Card:
  const handleCardClick = (card: any) => {
    setSingleCardData({ data: card, loading: false, id: null });
    setCreatorCardModal(true);
    getCreatorCardClips(card._id);
  };

  const onQuery = (
    checked: boolean,
    indentifier: string,
    label: string,
    isCardClick?: boolean,
    isCancelSingleClick?: boolean,
  ) => {
    // Handle true / false query params

    const myCards = indentifier === "myCards";
    const isBooleanIdentifier = ["proOnly", "myCards"].includes(indentifier);

    if (isBooleanIdentifier) {
      const queryUpdate = {
        ...query,
        [indentifier]: checked,
      };

      setBanner(
        queryUpdate?.type?.length === 1 ? queryUpdate.type[0] : "default",
      );

      scrollToTop();
      return setQuery(queryUpdate);
      // return facetRefetch(queryUpdate);
    }

    let queryUpdate: any = Object.assign({}, query);
    if (isCardClick) {
      // console.log({ isCardClick, label, indentifier });
      if (
        queryUpdate[indentifier] &&
        queryUpdate[indentifier].includes(label)
      ) {
        // console.log("not running dup query");
        // Dont re issue query for double clicking...
        return;
      }

      // re assign query object only after we validate its not there:
      queryUpdate = {};

      // Stop gap would be to not do above for remix:
      // ref: https://playsharestar.atlassian.net/browse/AB-429
    }

    // console.log(`reassign : ${JSON.stringify(queryUpdate)}`);
    // this clears the selections from our current identifier. Makes it so only one thing from each identifier is selectable
    if (!isCancelSingleClick && !isBooleanIdentifier)
      queryUpdate[indentifier] = undefined;
    if (queryUpdate[indentifier] && validSearch.includes(indentifier)) {
      // if checked is true, we add it to the array or create it:
      if (checked) queryUpdate[indentifier].push(label);
      else {
        // if there is only 1, delete it from the query:
        if (queryUpdate[indentifier].length === 1) {
          queryUpdate[indentifier] = undefined;
        } else {
          // else just filter out the label:
          queryUpdate[indentifier] = queryUpdate[indentifier].filter(
            (x: any) => x !== label,
          );
        }
      }
    } else if (!isBooleanIdentifier) {
      queryUpdate[indentifier] = [label];
    }

    if (label === "function-killAllIdentifiers") {
      queryUpdate[indentifier] = undefined;
    }

    setBanner(
      queryUpdate?.type?.length === 1 ? queryUpdate.type[0] : "default",
    );
    queryToUrl(queryUpdate, pageNav);

    setQuery(queryUpdate as TFacetQueryType);

    facetRefetch({
      variables: {
        ...queryUpdate,
        option2: true,
        myCards: myCards,
        getUserCards: false,
      },
    });
    scrollToTop();
    return;
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const clear = () => {
    setQuery({});
    queryToUrl({}, pageNav);
    return window.history.replaceState(null, "", `${pageNav}`);
  };

  const handleCardToggle = (card: TCreatorCard, isActive: boolean) => {
    // console.log("handle card toggle");
    // console.log(allstarUser, cardScopes);

    if (!allstarUser.loggedIn) {
      window.rudderanalytics.track("Allstar Studio - Visitor Attempt Toggle");
      setAllstarModalState({
        ...allstarModalState,
        isOpen: ModalType.Signup,
        data: {
          action: "signup",
        },
      });

      return false;
    }

    if (!hasScope(allstarUser.user, card.scopeUse) && card.ui.badge === "top") {
      setModal({ type: "top" });
      return false;
    }

    if (!hasScope(allstarUser.user, card.scopeUse)) {
      setModal({ type: card.ui.rarity });
      return false;
    }

    if (isActive) {
      setStudioSession({
        ...studioSession,
        userActivated: [...studioSession.userActivated, card],
      });
    } else {
      setStudioSession({
        ...studioSession,
        userActivated: studioSession.userActivated.filter(
          (x: any) => x._id !== card._id,
        ),
      });
    }
    toggleCardState({ variables: { isActive, cardIds: [card._id] } });
    window.rudderanalytics.track("Creator Card Toggle", {
      title: card?.ui?.title,
      cardId: card?._id,
      state: isActive ? "on" : "off",
      isAndroidApp: isMobileApp,
      remix: false,
    });

    return true;
  };

  //Enable/Disable all hits for anything other than homepage
  //homepage will pass in cardIds to enable
  const handleToggleAll = (
    isActive: boolean,
    category?: string,
    cards?: any[],
  ) => {
    if (!allstarUser.loggedIn) {
      setAllstarModalState({
        ...allstarModalState,
        isOpen: ModalType.Signup,
        data: {
          action: "signup",
        },
      });
      return false;
    }

    const ids = cards
      ? cards
          .filter((x: any) => {
            if (
              isActive &&
              hasScope(allstarUser.user, x.scopeUse) &&
              !studioSession.userActivated.includes(x._id)
            )
              return true;

            if (!isActive) return true;

            return false;
          })
          .map((x: any) => {
            return x._id;
          })
      : cardsData.hits
          .filter((x: any) => {
            if (
              isActive &&
              hasScope(allstarUser.user, x.scopeUse) &&
              !studioSession.userActivated.includes(x._id)
            )
              return true;
            if (!isActive) return true;
            return false;
          })
          .map((x: any) => {
            return x._id;
          });

    const cardsToApply = cards ? cards : cardsData.hits;

    if (isActive && isArray(cardsToApply)) {
      const filtered = cardsToApply.filter((x: any) => {
        return ids.includes(x._id);
      });
      setStudioSession({
        ...studioSession,
        userActivated: [...studioSession.userActivated, ...filtered],
      });
    } else if (!isActive && isArray(cardsToApply)) {
      //remove all cards where the cards id matches the id of the cards passed in

      setStudioSession({
        ...studioSession,
        userActivated: studioSession.userActivated.filter(
          (x: any) => !ids.includes(x._id),
        ),
      });
    }

    toggleCardState({
      variables: {
        isActive,
        cardIds: ids,
      },
    });

    return true;
  };

  const clearEnabledCards: any = () => {
    clearMyEnabledCards();
  };

  switch (route) {
    case ERoute.Remix:
      return (
        <RemixView
          modal={modal}
          setModal={setModal}
          clear={clear}
          query={query}
          onQuery={onQuery}
          handleCardClick={handleCardClick}
          handleCardToggle={handleCardToggle}
          facetLoading={facetLoading}
          cardsData={cardsData}
          cardClips={cardClips}
          singleCardData={singleCardData}
          creatorCardModal={creatorCardModal}
          setCreatorCardModal={setCreatorCardModal}
          allstarUser={allstarUser}
          getFreshProfile={getFreshProfile}
          logout={logout}
          remixData={remixData}
          banner={banner}
          postRemix={postRemix}
          pageNav={pageNav}
          mutateFollowers={mutateFollowers}
          handleToggleAll={handleToggleAll}
        />
      );
    case ERoute.Studio:
      return (
        <StudioView
          modal={modal}
          setModal={setModal}
          clear={clear}
          query={query}
          onQuery={onQuery}
          handleCardClick={handleCardClick}
          handleCardToggle={handleCardToggle}
          facetLoading={facetLoading || cardsData.loading}
          cardsData={cardsData}
          singleCardData={singleCardData}
          cardClips={cardClips}
          creatorCardModal={creatorCardModal}
          setCreatorCardModal={setCreatorCardModal}
          allstarUser={allstarUser}
          getFreshProfile={getFreshProfile}
          logout={logout}
          banner={banner}
          pageNav={pageNav}
          mutateFollowers={mutateFollowers}
          clearEnabledCards={clearEnabledCards}
          handleToggleAll={handleToggleAll}
          postRemix={postRemix}
        />
      );
  }
}
